// The app shell — TODO T17 (30 Aug 2026), rebuilt to v2 at T30 (1 Sep 2026); the nav counts, the
// ⌘K palette and kadmon's pill at T36 (2 Sep 2026).
//
// v2's sidebar (the logo lockup, the "Workspace" micro label, the accent-rail nav, the user
// block), its frosted sticky top bar, and its off-canvas navigation under 1000px — which is the
// whole of "usable on a phone" for getting between screens (design doc § 3).
//
// THE NAV IS STILL ONLY WHAT EXISTS. T17's rule holds: a nav item that leads nowhere is a promise
// the product does not keep — which is why Reports appeared in the nav at T32 (3 Sep 2026) and
// not when its slot line was reserved.
//
// THE NUMBERS ON IT ARE COUNTED, in one poll. `GET /api/v1/counts` answers the nav counts, the
// feed's unread badge and "kadmon · N replies today" together (design doc § T36.2 and .8), because
// the shell is on every screen and each number would otherwise be its own request per recruiter
// per interval against a service sized on seats.
import { SignOutButton } from '@clerk/react';
import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useCounts } from '../api/activity.ts';
import { useMe } from '../api/me.ts';
import darkLogo from '../assets/kadmon-logo-dark.png';
import lightLogo from '../assets/kadmon-logo-light.png';
import { Avatar, Icon, FirmName, VeraOrb } from '../ui/atoms.tsx';
import { VoiceOrb } from './VoiceOrb.tsx';
import { NAV, childActive, groupOpen, type NavCount } from './nav.ts';
import { VeraPanel } from './VeraPanel.tsx';
import { Freshness } from './Freshness.tsx';
import { NewVersionStrip } from './NewVersion.tsx';
import { Palette } from './Palette.tsx';
import { useScrollLock } from './scrollLock.ts';
import { AccountAppearance } from './theme.tsx';

/** The build's version, from package.json — see vite.config.ts. Shown in the footer, alone. */
declare const __CONSOLE_VERSION__: string;

export function Shell() {
  const me = useMe();
  const counts = useCounts();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  // The off-canvas drawer owns the screen while it is open; the page under it stays put.
  useScrollLock(navOpen);

  // Escape closes the drawer and the palette; ⌘K (Ctrl+K) toggles the palette; growing past the
  // breakpoint closes the drawer too, or it would still be "open" as a full-width panel over a
  // layout that no longer needs one.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setNavOpen(false);
        setPaletteOpen(false);
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setNavOpen(false);
        setPaletteOpen((open) => !open);
      }
    };
    const onResize = () => {
      if (window.innerWidth > 1000) setNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return (
    <div className="app" data-nav={navOpen ? 'open' : 'closed'}>
      <AccountAppearance />
      <div className="nav-scrim" onClick={() => setNavOpen(false)} aria-hidden={!navOpen} />

      <aside className="app-aside">
        {/* kadmon's logotype, cut from Natanel's vector PDFs and never redrawn (T64, T382): the light one on the
            dark theme, the dark one on the light. The theme shows one and hides the other. */}
        <NavLink to="/searches" className="sidebar__brand" onClick={() => setNavOpen(false)}>
          <VoiceOrb state="listening" size={30} className="brand-orb" />
          <span>
            <img className="sidebar__logo sidebar__logo--on-dark" src={lightLogo} alt="kadmon" />
            <img className="sidebar__logo sidebar__logo--on-light" src={darkLogo} alt="kadmon" />
            <span className="sidebar__eyebrow">Brain</span>
          </span>
        </NavLink>

        <div className="sidebar__section">Workspace</div>
        <nav className="nav">
          {NAV.map((item) => {
            const open = groupOpen(item, location.pathname);
            return (
              <div key={item.to} className="nav-group">
                <NavLink
                  to={item.to}
                  onClick={() => setNavOpen(false)}
                  className={({ isActive }) =>
                    // A group is the page open while any of its sub-items is (Candidates' own
                    // path is Searches', which is not every page under it).
                    isActive || (item.under !== undefined && open)
                      ? 'nav-item nav-item--active'
                      : 'nav-item'
                  }
                >
                  <span className="nav-item__rail" />
                  <Icon name={item.icon} size={14} className="nav-item__icon" />
                  <span className="nav-item__label">{item.label}</span>
                  {item.count && counts.data ? (
                    <NavCountBadge count={item.count(counts.data)} />
                  ) : (
                    <span />
                  )}
                </NavLink>
                {open && item.children && (
                  <div className="nav-sub" role="group" aria-label={item.label}>
                    {item.children.map((child) => (
                      <NavLink
                        key={child.to}
                        to={child.to}
                        end
                        onClick={() => setNavOpen(false)}
                        className={() =>
                          childActive(child, location.pathname)
                            ? 'nav-sub__item nav-sub__item--active'
                            : 'nav-sub__item'
                        }
                      >
                        <span>{child.label}</span>
                        {counts.data ? (
                          <span className="nav-item__count">{child.count(counts.data)}</span>
                        ) : (
                          <span />
                        )}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="sidebar__spacer" />

        <div className="sidebar__user">
          <Avatar name={me.data?.staffName ?? null} size={30} />
          <span style={{ flex: 1, minWidth: 0 }}>
            <span className="sidebar__user-name">{me.data?.staffName ?? '…'}</span>
            <span className="micro" style={{ display: 'block', fontSize: 9 }}>
              {me.data?.staffRole ?? ''}
            </span>
          </span>
          <SignOutButton>
            <button className="sidebar__signout" title="Sign out" aria-label="Sign out">
              <Icon name="logout" size={13} />
            </button>
          </SignOutButton>
        </div>
      </aside>

      <main className="main">
        <div className="topbar">
          <div className="topbar__left">
            <button
              className="nav-toggle"
              onClick={() => setNavOpen((open) => !open)}
              aria-label={navOpen ? 'Close navigation' : 'Open navigation'}
              aria-expanded={navOpen}
            >
              <Icon name={navOpen ? 'x' : 'menu'} size={15} />
            </button>

            {/* The field is a button: the palette is where typing happens. Its label says the
                three things the palette finds — and, since T35, that a sentence goes to kadmon. */}
            <button
              type="button"
              className="topbar__search"
              onClick={() => setPaletteOpen(true)}
              aria-label="Search candidates, searches and clients, or ask kadmon"
              aria-keyshortcuts="Meta+K Control+K"
            >
              <Icon name="search" size={14} />
              <span className="topbar__search-hint">
                Search candidates, searches, clients, or ask kadmon
              </span>
              <span className="kbd">⌘K</span>
            </button>
          </div>
          <div className="topbar__right">
            <VeraStatus replies={counts.data?.repliesToday} />
            <Freshness />
          </div>
        </div>

        <div className="content">
          <Outlet />
        </div>

        <footer className="footer">
          <span className="micro">
            <FirmName /> · brain
          </span>
          {/* The version and nothing else. The demo's footer carried compliance claims; they are
              placeholders until legal signs off on Annex III, and a certification-shaped sentence
              in a working product is worse than no sentence (design doc § 1.6). */}
          <span className="micro mono">v{__CONSOLE_VERSION__}</span>
        </footer>
      </main>

      {paletteOpen && <Palette onClose={() => setPaletteOpen(false)} />}
      {/* The find drawer (T35) lives in the URL (`?find=`), so it outlives the palette that opens
          it, and the search page's button opens the same one. */}
      <VeraPanel />
      {/* A newer brain is live (src/version.ts): the strip stays until the tab reloads. */}
      <NewVersionStrip />
    </div>
  );
}

function NavCountBadge({ count }: { count: NavCount }) {
  // An empty count is no pill at all (T91: Proposed says nothing while nothing waits).
  if (count === '') return <span />;
  if (typeof count === 'string') return <span className="nav-item__count">{count}</span>;
  return (
    <span
      className={count.alert ? 'nav-item__count nav-item__count--alert' : 'nav-item__count'}
      aria-label={`${count.n} ${count.word}`}
    >
      <span className="nav-item__count-n">{count.n}</span>
      <span className="nav-item__count-word">{count.word}</span>
    </span>
  );
}

/**
 * "kadmon · N replies today" — counted from today's outbound agent messages (design doc § T36.8),
 * where "today" starts at this browser's midnight (api/activity.ts `startOfLocalDay`). A dash
 * until the first poll answers, never a placeholder number.
 */
function VeraStatus({ replies }: { replies: number | undefined }) {
  return (
    <span className="topbar__status">
      <VeraOrb size={9} />
      <span>
        kadmon <span className="dim">·</span> <span className="mono">{replies ?? '-'}</span> replies
        today
      </span>
    </span>
  );
}
